import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchPlayer, fetchUserTeams } from "../lib/api";
import { HoloPlayerCard } from "./HoloPlayerCard";
import { TierBadge } from "./TierBadge";
import { TeamHqModal } from "./TeamHqModal";
import { DmModal } from "./DmModal";
import { ReportPlayerModal } from "./ReportPlayerModal";

/**
 * PlayerPublicPage — read-only public dashboard for any player at
 * `/players/:user_id`. Shows their holo card, top teams, and per-game
 * stats. The viewer can interact with the player via the holo card's
 * action buttons (DM, FRIEND, REPORT) — when signed out these route
 * through `onRequireAuth` so the sign-in modal opens instead of silently
 * doing nothing.
 *
 * `user_id` accepts either the canonical `user_xxxx` ID or a URL-safe
 * handle derived from the player's display name (e.g. `parisdelux`
 * matches "Paris Delux"). Resolution happens server-side.
 */
export function PlayerPublicPage({ onToast, onRequireAuth, user }) {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [teams, setTeams] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [hqTeamId, setHqTeamId] = useState(null);
  const [dmTarget, setDmTarget] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchPlayer(user_id).catch((e) => { throw e; }),
      fetchUserTeams(user_id).catch(() => []),
    ])
      .then(([p, t]) => { setPlayer(p); setTeams(t || []); setErr(""); })
      .catch((e) => setErr(e?.response?.data?.detail || "Player not found."))
      .finally(() => setLoading(false));
  }, [user_id]);

  if (loading) {
    return (
      <main className="page-z" data-testid="player-page-loading">
        <div className="max-w-screen-xl mx-auto px-4 py-16 text-center">
          <p className="text-[11px] tracking-[3px] text-[#14b8a6] animate-pulse">LOADING OPERATOR…</p>
        </div>
      </main>
    );
  }
  if (err || !player) {
    return (
      <main className="page-z">
        <div className="max-w-screen-xl mx-auto px-4 py-16 text-center">
          <p className="text-[11px] tracking-[3px] text-[#14b8a6]" data-testid="player-page-error">
            {err || "Player not found."}
          </p>
          <button
            type="button"
            onClick={() => navigate("/players")}
            className="inline-block mt-4 text-[11px] tracking-widest text-[#14b8a6] hover:text-white"
          >
            ← BROWSE PLAYERS
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="page-z" data-testid="player-public-page">
      <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="player-page-back"
          className="text-[10px] tracking-widest text-white/45 hover:text-white mb-3"
        >
          <i className="fas fa-arrow-left mr-1" />BACK
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6 items-start">
          {/* Holo card — wire the action buttons through onRequireAuth so
              signed-out visitors get the sign-in modal instead of a silent
              no-op when they click FRIEND / MSG / REPORT PLAYER. */}
          <HoloPlayerCard
            p={player}
            isSelf={user?.user_id === player.user_id}
            onSendFriend={() => user ? onToast?.("Open a player listing to send a friend request.") : onRequireAuth?.()}
            onOpenDm={() => user ? setDmTarget(player) : onRequireAuth?.()}
            onReport={() => user ? setReportTarget(player) : onRequireAuth?.()}
          />

          {/* Stats column */}
          <div className="space-y-4">
            <div className="glass rounded-3xl p-6" data-testid="player-stats-block">
              <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-2">
                <i className="fas fa-chart-line mr-1" />OPERATOR STATS
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Stat label="MMR" value={player.elo ?? "—"} />
                <Stat label="GAMES PLAYED" value={(player.games_played || []).length} />
                <Stat label="TYPE" value={(player.user_type || "casual").toUpperCase()} />
                <Stat label="REGION" value={player.region || "—"} />
              </div>
            </div>

            <div className="glass rounded-3xl p-6">
              <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-3">
                <i className="fas fa-shield-halved mr-1" />TEAMS · {teams.length}
              </p>
              {teams.length === 0 ? (
                <p className="text-[11px] text-white/45">Not on any team yet.</p>
              ) : (
                <div className="space-y-2">
                  {teams.slice(0, 8).map((t) => (
                    <button
                      key={t.team_id}
                      type="button"
                      onClick={() => setHqTeamId(t.team_id)}
                      data-testid={`player-team-${t.team_id}`}
                      className="w-full text-left flex items-center gap-2 p-2 rounded-xl bg-black/30 hover:bg-[#14b8a6]/15 border border-transparent hover:border-[#14b8a6]/40 transition-colors"
                    >
                      <span className="flex-1 min-w-0 text-white/85 text-[12px] truncate">
                        {t.name}
                        {t.game && (
                          <span
                            data-testid={`player-team-${t.team_id}-game`}
                            className="ml-1.5 text-[9px] tracking-widest text-[#14b8a6]"
                          >
                            · {t.game.toUpperCase()}
                          </span>
                        )}
                      </span>
                      {t.tier && <TierBadge tier={t.tier} mmr={t.mmr} compact />}
                      <i className="fas fa-up-right-from-square text-[12px] text-[#14b8a6]" title="Open Team" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {hqTeamId && (
        <TeamHqModal
          teamId={hqTeamId}
          onClose={() => setHqTeamId(null)}
          onToast={onToast}
        />
      )}
      {dmTarget && (
        <DmModal
          targetUser={dmTarget}
          onClose={() => setDmTarget(null)}
          onToast={onToast}
        />
      )}
      <ReportPlayerModal
        open={!!reportTarget}
        player={reportTarget}
        onClose={() => setReportTarget(null)}
        onToast={onToast}
      />
    </main>
  );
}

function Stat({ label, value }) {
  return (
    <div className="glass rounded-2xl p-3 border border-white/5">
      <p className="text-[9px] tracking-[3px] text-white/40">{label}</p>
      <p className="text-white font-bold text-lg mt-1">{value}</p>
    </div>
  );
}

export default PlayerPublicPage;
